import React, { useEffect, useRef, useState } from "react";
import FolderIcon from "@material-ui/icons/FolderOutlined";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DriveFileMoveIcon from "@material-ui/icons/SubdirectoryArrowRight";

/**
 * Bulk-action bar. Appears as soon as anything is selected and reports exactly
 * what is selected, so "Move" is never ambiguous about what it will act on.
 *
 * Folders cannot be nested (the folder schema has no parent), so "Move to
 * folder" applies to the selected projects only. That is stated in the bar
 * rather than silently ignoring the folders in the selection.
 */
export default function SelectionToolbar({
  projectCount,
  folderCount,
  folders,
  inFolder,
  onMoveToFolder,
  onDeleteFolders,
  onClear,
  busy,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  const parts = [];
  if (projectCount)
    parts.push(`${projectCount} ${projectCount === 1 ? "project" : "projects"}`);
  if (folderCount)
    parts.push(`${folderCount} ${folderCount === 1 ? "folder" : "folders"}`);

  const canMove = projectCount > 0;

  return (
    <div className="selection-toolbar" role="region" aria-label="Bulk actions">
      <div className="selection-toolbar-count">
        <span className="selection-toolbar-badge">
          {projectCount + folderCount}
        </span>
        <span>{parts.join(" · ")} selected</span>
        {folderCount > 0 && projectCount > 0 && (
          <span className="selection-toolbar-hint">
            Move applies to projects only
          </span>
        )}
      </div>

      <div className="selection-toolbar-actions">
        <div className="selection-move-wrap" ref={menuRef}>
          <button
            type="button"
            className="selection-btn selection-btn-primary"
            disabled={!canMove || busy}
            title={
              canMove
                ? "Move the selected projects into a folder"
                : "Select at least one project to move"
            }
            onClick={() => setMenuOpen((o) => !o)}
          >
            <DriveFileMoveIcon style={{ fontSize: "17px" }} />
            {busy ? "Moving…" : "Move to folder"}
          </button>

          {menuOpen && (
            <div className="selection-move-menu">
              <div className="selection-move-menu-header">
                Move {projectCount} {projectCount === 1 ? "project" : "projects"}{" "}
                to
              </div>
              <div className="selection-move-menu-list">
                {inFolder && (
                  <button
                    type="button"
                    className="selection-move-menu-item"
                    onClick={() => {
                      setMenuOpen(false);
                      onMoveToFolder(null);
                    }}
                  >
                    <span className="selection-move-menu-root">↑</span>
                    All Projects (root)
                  </button>
                )}
                {folders.map((f) => (
                  <button
                    type="button"
                    key={f._id}
                    className="selection-move-menu-item"
                    onClick={() => {
                      setMenuOpen(false);
                      onMoveToFolder(f._id);
                    }}
                  >
                    <FolderIcon style={{ fontSize: "16px", color: "#7a7a83" }} />
                    <span className="selection-move-menu-name">{f.name}</span>
                    <span className="selection-move-menu-count">
                      {f.projectCount}
                    </span>
                  </button>
                ))}
                {folders.length === 0 && !inFolder && (
                  <div className="selection-move-menu-empty">
                    No folders yet — create one first.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {folderCount > 0 && (
          <button
            type="button"
            className="selection-btn selection-btn-danger"
            disabled={busy}
            onClick={onDeleteFolders}
            title="Delete the selected folders. Projects inside move back to All Projects."
          >
            <DeleteOutlineIcon style={{ fontSize: "17px" }} />
            Delete {folderCount === 1 ? "folder" : "folders"}
          </button>
        )}

        <button
          type="button"
          className="selection-btn selection-btn-ghost"
          onClick={onClear}
          disabled={busy}
        >
          <CloseIcon style={{ fontSize: "16px" }} />
          Clear
        </button>
      </div>
    </div>
  );
}
